import '../../Admin.scss';
import './EditChapters.scss';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { Button, IconButton, Input, LoadingPlaceholder, Menu, MenuItem, Select } from '@trackman/web-shared-components';
import { ReactComponent as Plus } from 'images/icons/Plus.svg';
import { ReactComponent as Trash } from 'images/icons/Trash.svg';
import { ReactComponent as Pencil } from 'images/icons/Pencil.svg';
import { ReactComponent as ChevronDown } from 'images/icons/ChevronDownThin.svg';
import { ReactComponent as ArrowDownIcon } from 'images/icons/ArrowDown.svg';
import { ReactComponent as ArrowUpIcon } from 'images/icons/ArrowUp.svg';
import { ReactComponent as SortingArrow } from 'images/icons/SortingArrow.svg';
import Paragraph from 'components/TextComps/Paragraph';
import SectionTitle from 'components/TextComps/SectionTitle';
import React, { useCallback, useState } from 'react';
import {
  BaseAdminChapterTest,
  AdminChapter,
  AdminChapterStatus,
  AdminChapterTest,
  ExistingAdminChapterTest,
  AdminEditedLesson,
  AdminLesson,
  ExistingAdminLesson,
  BaseAdminLesson,
} from 'types/admin/modules';
import { EditLessonDialog } from './ManageLessons/EditLessonDialog/EditLessonDialog';
import { useChaptersStore } from 'stores/Admin/Editors/ModuleEditor/ChaptersStore';
import { AddExistingLessonsDialog } from './ManageLessons/AddExistingLessonsDialog/AddExistingLessonsDialog';

export const EditChapters = () => {
  const chapters: AdminChapter[] = useChaptersStore((state) => state.chapters);
  const loadingInitialData: boolean = useChaptersStore((state) => state.initializing);
  const loading: boolean = useChaptersStore((state) => state.loading);
  const addChapter = useChaptersStore((state) => state.addChapter);
  const deleteChapter = useChaptersStore((state) => state.deleteChapter);
  const updateChapterTitle = useChaptersStore((state) => state.updateChapterTitle);
  const updateChapterStatus = useChaptersStore((state) => state.updateChapterStatus);
  const addLesson = useChaptersStore((state) => state.addLesson);
  const deleteLesson = useChaptersStore((state) => state.deleteLesson);
  const addChapterTest = useChaptersStore((state) => state.addChapterTest);
  const updateLesson = useChaptersStore((state) => state.updateLesson);
  const updateChapterTest = useChaptersStore((state) => state.updateChapterTest);
  const sortChapters = useChaptersStore((state) => state.sortChapters);
  const sortLessons = useChaptersStore((state) => state.sortLessons);

  const [expandedPanelIds, setExpandedPanelIds] = useState<number[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [openedMenuIndex, setOpenedMenuIndex] = useState<number | null>(null);
  const [openedEditLessonModalIndex, setOpenedEditLessonModalIndex] = useState<number | undefined>(undefined);
  const [openedExistingLessonModalIndex, setOpenedExistingLessonModalIndex] = useState<number | undefined>(undefined);
  const [currentlyEditedLesson, setCurrentlyEditedLesson] = useState<AdminEditedLesson | ExistingAdminChapterTest | undefined>(undefined);
  const [currentlyEditedLessonIndex, setCurrentlyEditedLessonIndex] = useState<number | undefined>(undefined);

  const chapterStatus: string[] = ['Hidden', 'Visible'];

  const handleClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const toggleSinglePanel = useCallback(
    (panelId: number) => () => {
      if (expandedPanelIds.includes(panelId)) {
        setExpandedPanelIds(expandedPanelIds.filter((id) => id !== panelId));
      } else {
        setExpandedPanelIds([...expandedPanelIds, panelId]);
      }
    },
    [expandedPanelIds]
  );

  const toggleAllPanels = useCallback(() => {
    if (expandedPanelIds.length !== 0) {
      setExpandedPanelIds([]);
    } else {
      const panelIdsToOpen: number[] = chapters?.map((chapter, index) => index);
      setExpandedPanelIds([...panelIdsToOpen]);
    }
  }, [expandedPanelIds, chapters]);

  const openMenu = useCallback((event: React.SyntheticEvent<HTMLButtonElement>, index: number) => {
    setOpenedMenuIndex(index);
    setMenuAnchor(event.currentTarget);
  }, []);

  const closeMenu = useCallback(() => {
    setOpenedMenuIndex(null);
    setMenuAnchor(null);
  }, []);

  const selectMenuOption = useCallback((type: 'add' | 'existing', index: number) => {
    if (type === 'add') {
      setOpenedEditLessonModalIndex(index);
    } else {
      setOpenedExistingLessonModalIndex(index);
    }
  }, []);

  const handleUpdateChapterLessons = useCallback(
    (chapterIndex: number, payload: AdminLesson | AdminChapterTest | undefined) => {
      if (payload !== undefined) {
        if (currentlyEditedLesson) {
          if (currentlyEditedLessonIndex !== undefined) {
            updateLesson(chapterIndex, currentlyEditedLessonIndex, payload as AdminLesson);
          } else {
            updateChapterTest(chapterIndex, payload as AdminChapterTest);
          }
        } else {
          if (payload.kind !== undefined) {
            addLesson(chapterIndex, [payload]);
          } else {
            addChapterTest(chapterIndex, payload);
          }
        }
      }

      setOpenedEditLessonModalIndex(undefined);
      setCurrentlyEditedLesson(undefined);
      setCurrentlyEditedLessonIndex(undefined);
      closeMenu();
    },
    [currentlyEditedLesson, currentlyEditedLessonIndex]
  );

  const handleAddExistingLessonToChapter = useCallback((chapterIndex: number, payload: ExistingAdminLesson[] | undefined) => {
    if (payload !== undefined) {
      addLesson(chapterIndex, payload);
    }

    setOpenedExistingLessonModalIndex(undefined);
    closeMenu();
  }, []);

  const startEditingLesson = useCallback(
    (lesson: AdminEditedLesson | ExistingAdminChapterTest, chapterIndex: number, lessonIndex?: number) => {
      setCurrentlyEditedLesson(lesson);
      setCurrentlyEditedLessonIndex(lessonIndex);

      setOpenedEditLessonModalIndex(chapterIndex);
    },
    []
  );

  return (
    <div className='chapters-content-container'>
      {!loadingInitialData ? (
        <>
          {chapters?.length > 0 && (
            <div className='chapter-list-header'>
              <Button
                variant='contained'
                intent='secondary'
                startIcon={<Plus className='icon' />}
                disabled={loading}
                onClick={() => addChapter()}
              >
                Add Chapter
              </Button>
              <span className='collapse-all' onClick={toggleAllPanels}>
                {expandedPanelIds.length === 0 ? 'Expand' : 'Collapse'} all
              </span>
            </div>
          )}

          {chapters?.length > 0 ? (
            <div>
              {chapters.map((chapter, index) => (
                <Accordion key={index} expanded={expandedPanelIds.includes(index)} onChange={toggleSinglePanel(index)}>
                  <AccordionSummary expandIcon={<ChevronDown />}>
                    <div className='accordion-summary-content' onClick={handleClick}>
                      <div>
                        <Paragraph className='chapter-number'>
                          <>Chapter {index + 1}:</>
                        </Paragraph>
                        <Input
                          label='Chapter Title'
                          value={chapter.title ?? ''}
                          required
                          disabled={loading}
                          onChange={(e) => updateChapterTitle(index, e.currentTarget.value)}
                        />
                        <Select
                          label='Status'
                          value={chapter.status}
                          required
                          selectOptions={chapterStatus.map((type) => {
                            return {
                              label: type,
                              value: type,
                            };
                          })}
                          disabled={loading}
                          onChange={(e) => updateChapterStatus(index, e.target.value as AdminChapterStatus)}
                        />
                      </div>
                      <div>
                        <IconButton
                          id={`menu-${index}`}
                          className='plus-icon'
                          icon={() => <Plus />}
                          disabled={loading}
                          onClick={(ev) => openMenu(ev, index)}
                        />
                        <IconButton
                          icon={() => <ArrowUpIcon />}
                          style={index === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          disabled={loading}
                          onClick={() => (index !== 0 ? sortChapters(index, index - 1) : {})}
                        />
                        <IconButton
                          icon={() => <ArrowDownIcon />}
                          style={index === chapters.length - 1 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          disabled={loading}
                          onClick={() => (index !== chapters.length - 1 ? sortChapters(index, index + 1) : {})}
                        />
                        <IconButton icon={() => <Trash />} disabled={loading} onClick={() => deleteChapter(index)} />
                      </div>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className='chapter-content'>
                      {chapter.lessons &&
                        chapter.lessons.map((entry, lessonIndex) => (
                          <div key={lessonIndex}>
                            <div>
                              <span>{entry?.title}</span>
                              <span>{entry.kind === 'Picture' ? 'Image' : entry.kind}</span>
                            </div>
                            <div>
                              <Pencil
                                style={loading ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                onClick={() => startEditingLesson(entry as AdminEditedLesson, index, lessonIndex)}
                              />
                              <SortingArrow
                                style={lessonIndex === 0 || loading ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                onClick={() => (lessonIndex !== 0 && !loading ? sortLessons(index, lessonIndex, lessonIndex - 1) : {})}
                              />
                              <SortingArrow
                                className='sorting-arrow-down'
                                style={lessonIndex === chapter.lessons.length - 1 || loading ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                onClick={() =>
                                  lessonIndex !== chapter.lessons.length - 1 && !loading
                                    ? sortLessons(index, lessonIndex, lessonIndex + 1)
                                    : {}
                                }
                              />
                              <Trash
                                style={loading ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                onClick={() => deleteLesson(index, lessonIndex)}
                              />
                            </div>
                          </div>
                        ))}
                      {chapter.chapterTest ? (
                        <div>
                          <div>
                            <span>{chapter.chapterTest?.title}</span>
                            <span>Chapter Test</span>
                          </div>
                          <div>
                            <Pencil onClick={() => startEditingLesson(chapter.chapterTest as ExistingAdminChapterTest, index)} />
                            <Trash />
                          </div>
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </AccordionDetails>
                  {openedEditLessonModalIndex === index ? (
                    <EditLessonDialog
                      open={openedEditLessonModalIndex === index}
                      onClose={(lesson: BaseAdminLesson | BaseAdminChapterTest | undefined) => handleUpdateChapterLessons(index, lesson)}
                      editedLesson={currentlyEditedLesson}
                    />
                  ) : (
                    <></>
                  )}
                  {openedExistingLessonModalIndex === index ? (
                    <AddExistingLessonsDialog
                      open={openedExistingLessonModalIndex === index}
                      onClose={(payload: ExistingAdminLesson[] | undefined) => handleAddExistingLessonToChapter(index, payload)}
                    />
                  ) : (
                    <></>
                  )}
                </Accordion>
              ))}
              {
                <ChapterMenu
                  menuAnchor={menuAnchor}
                  open={openedMenuIndex !== null}
                  index={openedMenuIndex}
                  handleClose={closeMenu}
                  handleMenuOptionSelection={selectMenuOption}
                />
              }
            </div>
          ) : (
            <div className='empty-state-card'>
              <div>
                <SectionTitle>
                  <>No Chapters added yet.</>
                </SectionTitle>
                <Paragraph>
                  <>No Chapters have been added to this Module yet.</>
                </Paragraph>
                <Paragraph>
                  <>Create one to get started</>
                </Paragraph>
              </div>
              <Button
                variant='contained'
                intent='secondary'
                startIcon={<Plus className='icon' />}
                disabled={loading}
                onClick={() => addChapter()}
              >
                Add Chapter
              </Button>
            </div>
          )}
        </>
      ) : (
        <LoadingPlaceholder />
      )}
    </div>
  );
};

type ChapterMenuProps = {
  menuAnchor: null | HTMLElement;
  open: boolean;
  index: number | null;
  handleClose: () => void;
  handleMenuOptionSelection: (selectedOption: 'add' | 'existing', index: number) => void;
};

const ChapterMenu = ({ menuAnchor, open, index, handleClose, handleMenuOptionSelection }: ChapterMenuProps) => {
  return (
    <Menu
      anchorEl={menuAnchor}
      open={open}
      onClose={handleClose}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
    >
      <MenuItem onClick={() => index !== null && handleMenuOptionSelection('add', index)}>Create new lesson</MenuItem>
      <MenuItem onClick={() => index !== null && handleMenuOptionSelection('existing', index)}>Add existing lesson</MenuItem>
    </Menu>
  );
};
